import { createContext, forwardRef, useId, useEffect, useMemo, useContext, useState, cloneElement, useRef, useCallback } from 'react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Loader2, AlertCircle, AlertTriangle, CheckCircle2, Info, X, Lock, Pause, XCircle, Circle, ChevronDown, ChevronRight, Copy, RotateCcw, Check, Folder, Search, Plus, Upload, FileText, Wrench, Settings2, User, Bot, Paperclip, Mic, Square, Send, FolderOpen, Code2, Globe, TerminalSquare, Edit3, Hammer } from 'lucide-react';
import clsx from 'clsx';

// src/tokens/tokens.ts
var colors = {
  brand: {
    blue: {
      50: "#eff6ff",
      100: "#dbeafe",
      200: "#bfdbfe",
      300: "#93c5fd",
      400: "#60a5fa",
      500: "#3b82f6",
      600: "#2563eb",
      700: "#1d4ed8",
      800: "#1e40af",
      900: "#1e3a8a"
    },
    navy: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
      950: "#020617"
    },
    amber: {
      500: "#f59e0b",
      600: "#d97706"
    },
    emerald: {
      500: "#10b981",
      600: "#059669"
    },
    red: {
      500: "#ef4444",
      600: "#dc2626"
    }
  },
  block: {
    comm: "#6366f1",
    voice: "#14b8a6",
    bot: "#8b5cf6",
    speech: "#f97316",
    docs: "#10b981",
    solution: "#f59e0b",
    brain: "#94a3b8"
  }
};
var spacing = {
  0: "0",
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  8: "32px",
  10: "40px",
  12: "48px",
  16: "64px",
  20: "80px",
  24: "96px",
  32: "128px"
};
var radii = {
  none: "0",
  sm: "4px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  "2xl": "24px",
  pill: "9999px"
};
var fontSizes = {
  caption: "11px",
  bodySm: "13px",
  bodyMd: "15px",
  bodyLg: "17px",
  headingXs: "16px",
  headingSm: "18px",
  headingMd: "22px",
  headingLg: "28px",
  displaySm: "36px",
  displayMd: "48px",
  displayLg: "64px"
};
var fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700
};
var lineHeights = {
  tight: 1.15,
  snug: 1.3,
  normal: 1.5,
  relaxed: 1.6
};
var motion = {
  duration: {
    instant: "0ms",
    fast: "120ms",
    default: "200ms",
    slow: "320ms",
    deliberate: "480ms"
  },
  easing: {
    standard: "cubic-bezier(0.4, 0, 0.2, 1)",
    emphasized: "cubic-bezier(0.32, 0.72, 0, 1)",
    decelerate: "cubic-bezier(0, 0, 0.2, 1)",
    accelerate: "cubic-bezier(0.4, 0, 1, 1)",
    spring: "cubic-bezier(0.5, 1.4, 0.4, 1)"
  }
};
var zIndex = {
  base: 1,
  dropdown: 10,
  sticky: 50,
  overlay: 100,
  modal: 200,
  popover: 300,
  toast: 400,
  tooltip: 500,
  systemBanner: 600
};
var shadows = {
  none: "none",
  elev1: "0 2px 4px rgba(0,0,0,0.08)",
  elev2: "0 4px 12px rgba(0,0,0,0.12)",
  elev3: "0 12px 32px rgba(0,0,0,0.20)",
  glass1: "0 4px 16px rgba(0,0,0,0.12)",
  glass2: "0 8px 24px rgba(0,0,0,0.18)",
  glass3: "0 16px 40px rgba(0,0,0,0.24)"
};
var tokens = {
  colors,
  spacing,
  radii,
  fontSizes,
  fontWeights,
  lineHeights,
  motion,
  zIndex,
  shadows
};
var ALLOWED_OVERRIDES = /* @__PURE__ */ new Set([
  "--color-accent-primary",
  "--color-accent-primary-hover",
  "--color-accent-primary-active",
  "--color-accent-primary-on",
  "--color-fg-on-accent",
  "--font-family-display",
  "--font-family-body"
]);
var DesignSystemContext = createContext(void 0);
function DesignSystemProvider({
  theme = "dark",
  onThemeChange,
  tenantOverrides,
  rootElement,
  children
}) {
  useEffect(() => {
    const root = rootElement ?? (typeof document !== "undefined" ? document.documentElement : null);
    if (!root) return;
    root.setAttribute("data-theme", theme);
  }, [theme, rootElement]);
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!tenantOverrides) return;
    const filtered = {};
    for (const [key, value] of Object.entries(tenantOverrides)) {
      if (ALLOWED_OVERRIDES.has(key)) {
        filtered[key] = value;
      } else {
        console.warn(
          `[DesignSystemProvider] Tenant override "${key}" is not in allowlist; ignored.`
        );
      }
    }
    const cssText = Object.entries(filtered).map(([k, v]) => `  ${k}: ${v};`).join("\n");
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-iam-tenant-overrides", "");
    styleEl.textContent = `:root {
${cssText}
}`;
    document.head.appendChild(styleEl);
    return () => {
      styleEl.remove();
    };
  }, [tenantOverrides]);
  const ctx = useMemo(
    () => ({
      theme,
      setTheme: (next) => onThemeChange?.(next),
      tenantOverrides
    }),
    [theme, onThemeChange, tenantOverrides]
  );
  return /* @__PURE__ */ jsx(DesignSystemContext.Provider, { value: ctx, children });
}
function useDesignSystem() {
  const ctx = useContext(DesignSystemContext);
  if (!ctx) {
    throw new Error("useDesignSystem must be used within <DesignSystemProvider>");
  }
  return ctx;
}
function cn(...inputs) {
  return clsx(...inputs);
}
var Button = forwardRef(function Button2({
  variant = "primary",
  size = "md",
  loading = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}, ref) {
  const isDisabled = disabled || loading;
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      type,
      className: cn(
        "ds-button",
        `ds-button--${variant}`,
        `ds-button--${size}`,
        fullWidth && "ds-button--full",
        loading && "ds-button--loading",
        className
      ),
      disabled: isDisabled,
      "aria-busy": loading || void 0,
      ...rest,
      children: [
        loading ? /* @__PURE__ */ jsx(Loader2, { className: "ds-button__spinner", "aria-hidden": "true" }) : iconLeft && /* @__PURE__ */ jsx("span", { className: "ds-button__icon", "aria-hidden": "true", children: iconLeft }),
        children && /* @__PURE__ */ jsx("span", { className: "ds-button__label", children }),
        iconRight && !loading && /* @__PURE__ */ jsx("span", { className: "ds-button__icon", "aria-hidden": "true", children: iconRight })
      ]
    }
  );
});
var Input = forwardRef(function Input2({ label, hint, error, prefix, suffix, size = "md", id, className, required, ...rest }, ref) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : void 0;
  const errorId = error ? `${inputId}-error` : void 0;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || void 0;
  return /* @__PURE__ */ jsxs("div", { className: cn("ds-input-field", `ds-input-field--${size}`, className), children: [
    label && /* @__PURE__ */ jsxs("label", { htmlFor: inputId, className: "ds-input-field__label", children: [
      label,
      required && /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "ds-input-field__required", children: "*" })
    ] }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn("ds-input-field__control", error && "ds-input-field__control--error"),
        "data-error": error ? "true" : void 0,
        children: [
          prefix && /* @__PURE__ */ jsx("span", { className: "ds-input-field__adornment", "aria-hidden": "true", children: prefix }),
          /* @__PURE__ */ jsx(
            "input",
            {
              ref,
              id: inputId,
              "aria-invalid": error ? true : void 0,
              "aria-describedby": describedBy,
              "aria-required": required || void 0,
              className: "ds-input-field__input",
              ...rest
            }
          ),
          suffix && /* @__PURE__ */ jsx("span", { className: "ds-input-field__adornment", "aria-hidden": "true", children: suffix })
        ]
      }
    ),
    hint && !error && /* @__PURE__ */ jsx("p", { id: hintId, className: "ds-input-field__hint", children: hint }),
    error && /* @__PURE__ */ jsx("p", { id: errorId, className: "ds-input-field__error", role: "alert", children: error })
  ] });
});
var GlassPanel = forwardRef(function GlassPanel2({ variant = "md", as = "div", className, children, ...rest }, ref) {
  const Component = as;
  return /* @__PURE__ */ jsx(
    Component,
    {
      ref,
      "data-glass": "true",
      className: cn("ds-glass-panel", `ds-glass-panel--${variant}`, className),
      ...rest,
      children
    }
  );
});
function Spinner({ size = 16, label = "Loading\u2026", className, ...rest }) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      role: "status",
      "aria-live": "polite",
      "aria-label": label,
      className: cn("ds-spinner", className),
      ...rest,
      children: [
        /* @__PURE__ */ jsx(Loader2, { width: size, height: size, "aria-hidden": "true" }),
        /* @__PURE__ */ jsx("span", { className: "ds-spinner__sr", children: label })
      ]
    }
  );
}
var IconButton = forwardRef(function IconButton2({ icon, size = "md", variant = "ghost", className, type = "button", ...rest }, ref) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      ref,
      type,
      className: cn(
        "ds-icon-button",
        `ds-icon-button--${size}`,
        `ds-icon-button--${variant}`,
        className
      ),
      ...rest,
      children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "ds-icon-button__icon", children: icon })
    }
  );
});
function Tooltip({ content, children, side = "top", delayMs = 200 }) {
  const tipId = useId();
  const [open, setOpen] = useState(false);
  let timer = null;
  const show = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => setOpen(true), delayMs);
  };
  const hide = () => {
    if (timer) clearTimeout(timer);
    setOpen(false);
  };
  const trigger = cloneElement(children, {
    "aria-describedby": tipId,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide
  });
  return /* @__PURE__ */ jsxs("span", { className: "ds-tooltip-wrap", children: [
    trigger,
    /* @__PURE__ */ jsx(
      "span",
      {
        role: "tooltip",
        id: tipId,
        className: cn("ds-tooltip", `ds-tooltip--${side}`, open && "ds-tooltip--open"),
        children: content
      }
    )
  ] });
}
var ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle
};
function Toast({
  title,
  description,
  variant = "info",
  durationMs = 4e3,
  onDismiss
}) {
  const Icon = ICONS[variant];
  useEffect(() => {
    if (!durationMs || durationMs <= 0) return;
    const t = setTimeout(() => onDismiss?.(), durationMs);
    return () => clearTimeout(t);
  }, [durationMs, onDismiss]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "status",
      "aria-live": variant === "danger" ? "assertive" : "polite",
      className: cn("ds-toast", `ds-toast--${variant}`),
      children: [
        /* @__PURE__ */ jsx(Icon, { className: "ds-toast__icon", "aria-hidden": "true" }),
        /* @__PURE__ */ jsxs("div", { className: "ds-toast__body", children: [
          title && /* @__PURE__ */ jsx("div", { className: "ds-toast__title", children: title }),
          description && /* @__PURE__ */ jsx("div", { className: "ds-toast__description", children: description })
        ] }),
        onDismiss && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435",
            className: "ds-toast__dismiss",
            onClick: onDismiss,
            children: /* @__PURE__ */ jsx(X, { size: 16, "aria-hidden": "true" })
          }
        )
      ]
    }
  );
}
function Badge({ tone = "neutral", size = "sm", children, className, ...rest }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("ds-badge", `ds-badge--${tone}`, `ds-badge--${size}`, className),
      ...rest,
      children
    }
  );
}
function Kbd({ children, className, ...rest }) {
  return /* @__PURE__ */ jsx("kbd", { className: cn("ds-kbd", className), ...rest, children });
}
function formatCurrency(value, currency, locale) {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 2
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}
function CostMeter({
  used,
  budget,
  currency = "USD",
  locale,
  warningThreshold = 0.8,
  breakdown,
  onWarning,
  onHardStop,
  state: stateOverride,
  compact = false,
  popoverContent,
  ariaLabel,
  size = "md",
  "data-testid": testId
}) {
  const popoverId = useId();
  const [open, setOpen] = useState(false);
  const usedSafe = Math.max(0, used);
  const budgetSafe = Math.max(0, budget);
  const ratio = budgetSafe > 0 ? usedSafe / budgetSafe : 0;
  const percent = Math.min(100, Math.round(ratio * 100));
  const computed = stateOverride ?? (budgetSafe === 0 ? "ok" : usedSafe >= budgetSafe ? "hardStop" : ratio >= warningThreshold ? "warning" : "ok");
  const warnedRef = useRef(false);
  const stoppedRef = useRef(false);
  useEffect(() => {
    if (!stateOverride) {
      if (computed === "warning" && !warnedRef.current) {
        warnedRef.current = true;
        onWarning?.({ used: usedSafe, budget: budgetSafe });
      }
      if (computed === "hardStop" && !stoppedRef.current) {
        stoppedRef.current = true;
        onHardStop?.({ used: usedSafe, budget: budgetSafe });
      }
      if (computed === "ok") {
        warnedRef.current = false;
        stoppedRef.current = false;
      }
    }
  }, [computed, stateOverride, onWarning, onHardStop, usedSafe, budgetSafe]);
  const usedLabel = formatCurrency(usedSafe, currency, locale);
  const budgetLabel = formatCurrency(budgetSafe, currency, locale);
  const aria = ariaLabel ?? `\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u0435\u0442\u0441\u044F ${usedLabel} \u0438\u0437 ${budgetLabel}, ${percent}%`;
  const breakdownContent = useMemo(() => {
    if (popoverContent) return popoverContent;
    if (!breakdown || breakdown.length === 0) {
      return /* @__PURE__ */ jsx("p", { className: "ds-cost-meter__empty", children: "\u041D\u0435\u0442 \u0432\u044B\u0437\u043E\u0432\u043E\u0432 \u0432 \u0442\u0435\u043A\u0443\u0449\u0435\u0439 \u0441\u0435\u0441\u0441\u0438\u0438" });
    }
    return /* @__PURE__ */ jsx("ul", { className: "ds-cost-meter__breakdown", children: breakdown.map((row) => /* @__PURE__ */ jsxs("li", { className: "ds-cost-meter__breakdown-row", children: [
      /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__breakdown-label", children: row.label }),
      row.units && /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__breakdown-units", children: row.units }),
      /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__breakdown-amount", children: formatCurrency(row.amount, currency, locale) })
    ] }, `${row.label}-${row.group ?? ""}`)) });
  }, [popoverContent, breakdown, currency, locale]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "data-testid": testId,
      className: cn(
        "ds-cost-meter",
        `ds-cost-meter--${size}`,
        `ds-cost-meter--${computed}`,
        compact && "ds-cost-meter--compact",
        open && "ds-cost-meter--open"
      ),
      children: [
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            role: "meter",
            "aria-valuenow": Math.round(usedSafe * 100) / 100,
            "aria-valuemin": 0,
            "aria-valuemax": budgetSafe || void 0,
            "aria-label": aria,
            "aria-haspopup": "dialog",
            "aria-expanded": open,
            "aria-controls": popoverId,
            onClick: () => setOpen((o) => !o),
            onMouseEnter: () => setOpen(true),
            onMouseLeave: () => setOpen(false),
            onFocus: () => setOpen(true),
            onBlur: () => setOpen(false),
            className: "ds-cost-meter__trigger",
            children: [
              computed === "warning" && /* @__PURE__ */ jsx(AlertTriangle, { "aria-hidden": "true", className: "ds-cost-meter__icon" }),
              computed === "hardStop" && /* @__PURE__ */ jsx(Lock, { "aria-hidden": "true", className: "ds-cost-meter__icon" }),
              /* @__PURE__ */ jsxs("span", { className: "ds-cost-meter__value", children: [
                usedLabel,
                budgetSafe > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__sep", children: " / " }),
                  budgetLabel
                ] })
              ] }),
              budgetSafe > 0 && /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__bar", "aria-hidden": "true", children: /* @__PURE__ */ jsx("span", { className: "ds-cost-meter__bar-fill", style: { width: `${percent}%` } }) }),
              !compact && budgetSafe > 0 && /* @__PURE__ */ jsxs("span", { className: "ds-cost-meter__percent", children: [
                percent,
                "%"
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsxs("span", { "aria-live": "polite", className: "ds-cost-meter__sr", children: [
          computed === "warning" && "\u0414\u043E\u0441\u0442\u0438\u0433\u043D\u0443\u0442 \u043F\u043E\u0440\u043E\u0433 80% \u0431\u044E\u0434\u0436\u0435\u0442\u0430",
          computed === "hardStop" && "\u0411\u044E\u0434\u0436\u0435\u0442 \u0438\u0441\u0447\u0435\u0440\u043F\u0430\u043D"
        ] }),
        /* @__PURE__ */ jsx(
          "div",
          {
            id: popoverId,
            role: "dialog",
            "aria-label": "\u0420\u0430\u0437\u0431\u0438\u0432\u043A\u0430 \u0440\u0430\u0441\u0445\u043E\u0434\u043E\u0432",
            className: "ds-cost-meter__popover",
            children: breakdownContent
          }
        )
      ]
    }
  );
}
function formatDuration(ms) {
  if (ms == null) return "";
  if (ms < 1e3) return `${Math.round(ms)} \u043C\u0441`;
  if (ms < 6e4) return `${(ms / 1e3).toFixed(1)} \u0441`;
  return `${(ms / 6e4).toFixed(1)} \u043C\u0438\u043D`;
}
var STATUS_ICON = {
  queued: Circle,
  running: Loader2,
  success: CheckCircle2,
  failed: XCircle,
  requires_approval: AlertTriangle,
  cancelled: Pause
};
function ToolCallTimeline({
  calls,
  initiallyExpandedIds = [],
  onRowClick,
  onRetry,
  onCopy,
  emptyState,
  compact = false,
  "data-testid": testId
}) {
  const [expanded, setExpanded] = useState(new Set(initiallyExpandedIds));
  if (calls.length === 0) {
    return /* @__PURE__ */ jsx("div", { "data-testid": testId, className: "ds-timeline ds-timeline--empty", children: emptyState ?? /* @__PURE__ */ jsx("p", { className: "ds-timeline__empty", children: "\u0417\u0434\u0435\u0441\u044C \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F timeline \u0432\u044B\u0437\u043E\u0432\u043E\u0432 \u0430\u0433\u0435\u043D\u0442\u0430." }) });
  }
  const toggle = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
    onRowClick?.(id, next.has(id));
  };
  const copyPayload = (payload) => {
    const json = typeof payload === "string" ? payload : JSON.stringify(payload, null, 2);
    if (onCopy) {
      onCopy(json);
    } else if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(json).catch(() => {
      });
    }
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "feed",
      "aria-busy": calls.some((c) => c.status === "running") || void 0,
      "data-testid": testId,
      className: cn("ds-timeline", compact && "ds-timeline--compact"),
      children: calls.map((call) => {
        const Icon = STATUS_ICON[call.status];
        const isOpen = expanded.has(call.id);
        const isSubAgent = call.parentId != null;
        return /* @__PURE__ */ jsxs(
          "article",
          {
            role: "article",
            "aria-labelledby": `tc-${call.id}-name`,
            className: cn(
              "ds-timeline__row",
              `ds-timeline__row--${call.status}`,
              isSubAgent && "ds-timeline__row--sub"
            ),
            children: [
              /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  className: "ds-timeline__row-trigger",
                  onClick: () => toggle(call.id),
                  "aria-expanded": isOpen,
                  children: [
                    /* @__PURE__ */ jsxs("span", { className: "ds-timeline__indicator", "aria-hidden": "true", children: [
                      /* @__PURE__ */ jsx(
                        Icon,
                        {
                          className: cn(
                            "ds-timeline__icon",
                            call.status === "running" && "ds-timeline__icon--spin"
                          )
                        }
                      ),
                      /* @__PURE__ */ jsx("span", { className: "ds-timeline__connector" })
                    ] }),
                    /* @__PURE__ */ jsxs("span", { className: "ds-timeline__row-body", children: [
                      /* @__PURE__ */ jsxs("span", { className: "ds-timeline__row-head", children: [
                        /* @__PURE__ */ jsx("span", { id: `tc-${call.id}-name`, className: "ds-timeline__name", children: call.name }),
                        call.durationMs != null && call.status !== "running" && /* @__PURE__ */ jsx("span", { className: "ds-timeline__duration", children: formatDuration(call.durationMs) }),
                        call.status === "running" && /* @__PURE__ */ jsx("span", { className: "ds-timeline__duration", children: "\u0432\u044B\u043F\u043E\u043B\u043D\u044F\u0435\u0442\u0441\u044F\u2026" }),
                        call.status === "queued" && /* @__PURE__ */ jsx("span", { className: "ds-timeline__duration", children: "\u0432 \u043E\u0447\u0435\u0440\u0435\u0434\u0438" }),
                        isOpen ? /* @__PURE__ */ jsx(ChevronDown, { size: 14, className: "ds-timeline__chev", "aria-hidden": "true" }) : /* @__PURE__ */ jsx(ChevronRight, { size: 14, className: "ds-timeline__chev", "aria-hidden": "true" })
                      ] }),
                      call.summary && /* @__PURE__ */ jsx("span", { className: "ds-timeline__summary", children: call.summary })
                    ] })
                  ]
                }
              ),
              isOpen && /* @__PURE__ */ jsxs("div", { className: "ds-timeline__expanded", children: [
                call.errorMessage && /* @__PURE__ */ jsx("div", { className: "ds-timeline__error", children: call.errorMessage }),
                call.input !== void 0 && /* @__PURE__ */ jsxs("details", { children: [
                  /* @__PURE__ */ jsx("summary", { children: "Input" }),
                  /* @__PURE__ */ jsx("pre", { className: "ds-timeline__pre", children: JSON.stringify(call.input, null, 2) })
                ] }),
                call.output !== void 0 && /* @__PURE__ */ jsxs("details", { open: true, children: [
                  /* @__PURE__ */ jsx("summary", { children: "Output" }),
                  /* @__PURE__ */ jsx("pre", { className: "ds-timeline__pre", children: JSON.stringify(call.output, null, 2) })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "ds-timeline__actions", children: [
                  (call.input !== void 0 || call.output !== void 0) && /* @__PURE__ */ jsxs(
                    "button",
                    {
                      type: "button",
                      onClick: () => copyPayload(call.output ?? call.input),
                      className: "ds-timeline__action",
                      children: [
                        /* @__PURE__ */ jsx(Copy, { size: 14, "aria-hidden": "true" }),
                        " \u041A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C"
                      ]
                    }
                  ),
                  call.status === "failed" && onRetry && /* @__PURE__ */ jsxs(
                    "button",
                    {
                      type: "button",
                      onClick: () => onRetry(call.id),
                      className: "ds-timeline__action",
                      children: [
                        /* @__PURE__ */ jsx(RotateCcw, { size: 14, "aria-hidden": "true" }),
                        " \u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C"
                      ]
                    }
                  )
                ] })
              ] })
            ]
          },
          call.id
        );
      })
    }
  );
}
function formatRemaining(ms) {
  if (ms <= 0) return "\u0438\u0441\u0442\u0435\u043A\u043B\u043E";
  const total = Math.floor(ms / 1e3);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
function ApprovalPrompt({
  request,
  onApprove,
  onDeny,
  mode = "modal",
  closeable = false,
  onDismiss,
  disabled = false,
  renderAffectedResources,
  labels,
  "data-testid": testId
}) {
  const [comment, setComment] = useState("");
  const [denyReason, setDenyReason] = useState("");
  const [denyMode, setDenyMode] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const dialogRef = useRef(null);
  const expiresMs = request.expiresAt ? new Date(request.expiresAt).getTime() - now : Infinity;
  const expired = expiresMs <= 0;
  useEffect(() => {
    if (!request.expiresAt) return;
    const interval = setInterval(() => setNow(Date.now()), 1e3);
    return () => clearInterval(interval);
  }, [request.expiresAt]);
  useEffect(() => {
    if (expired) onDismiss?.();
  }, [expired, onDismiss]);
  useEffect(() => {
    if (mode === "modal") {
      dialogRef.current?.focus();
    }
  }, [mode]);
  const handleApprove = async () => {
    setSubmitting("approve");
    try {
      await onApprove({ comment: comment.trim() || void 0 });
    } finally {
      setSubmitting(null);
    }
  };
  const handleDeny = async () => {
    if (!denyReason.trim()) {
      setDenyMode(true);
      return;
    }
    setSubmitting("deny");
    try {
      await onDeny({ reason: denyReason.trim() });
    } finally {
      setSubmitting(null);
    }
  };
  const titleLabel = labels?.title ?? "\u0422\u0440\u0435\u0431\u0443\u0435\u0442\u0441\u044F \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0435";
  const approveLabel = labels?.approveButton ?? "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C";
  const denyLabel = labels?.denyButton ?? "\u041E\u0442\u043A\u043B\u043E\u043D\u0438\u0442\u044C";
  const body = /* @__PURE__ */ jsxs(
    "div",
    {
      ref: dialogRef,
      role: mode === "modal" ? "alertdialog" : "region",
      "aria-label": titleLabel,
      "data-testid": testId,
      tabIndex: -1,
      className: cn("ds-approval", `ds-approval--${mode}`, expired && "ds-approval--expired"),
      children: [
        /* @__PURE__ */ jsxs("header", { className: "ds-approval__header", children: [
          /* @__PURE__ */ jsxs("span", { className: "ds-approval__title", children: [
            /* @__PURE__ */ jsx(AlertTriangle, { "aria-hidden": "true", className: "ds-approval__title-icon" }),
            titleLabel
          ] }),
          request.expiresAt && /* @__PURE__ */ jsxs("span", { className: "ds-approval__timer", "aria-live": "off", children: [
            "\u0438\u0441\u0442\u0435\u043A\u0430\u0435\u0442 \u0447\u0435\u0440\u0435\u0437 ",
            formatRemaining(expiresMs)
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "ds-approval__body", children: [
          /* @__PURE__ */ jsxs("div", { className: "ds-approval__field", children: [
            /* @__PURE__ */ jsx("span", { className: "ds-approval__label", children: "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u0435" }),
            /* @__PURE__ */ jsx("code", { className: "ds-approval__action", children: request.action })
          ] }),
          request.reason && /* @__PURE__ */ jsxs("div", { className: "ds-approval__field", children: [
            /* @__PURE__ */ jsx("span", { className: "ds-approval__label", children: "\u041E\u0431\u043E\u0441\u043D\u043E\u0432\u0430\u043D\u0438\u0435" }),
            /* @__PURE__ */ jsx("p", { className: "ds-approval__reason", children: request.reason })
          ] }),
          request.scopes && request.scopes.length > 0 && /* @__PURE__ */ jsxs("div", { className: "ds-approval__field", children: [
            /* @__PURE__ */ jsx("span", { className: "ds-approval__label", children: "\u0422\u0440\u0435\u0431\u0443\u0435\u043C\u044B\u0435 scopes" }),
            /* @__PURE__ */ jsx("ul", { className: "ds-approval__scopes", children: request.scopes.map((s) => /* @__PURE__ */ jsxs(
              "li",
              {
                className: cn("ds-approval__scope", s.elevated && "ds-approval__scope--elev"),
                children: [
                  /* @__PURE__ */ jsx("code", { children: s.name }),
                  s.elevated && /* @__PURE__ */ jsx("span", { className: "ds-approval__scope-tag", children: "elevated" })
                ]
              },
              s.name
            )) })
          ] }),
          request.affectedResources && request.affectedResources.length > 0 && /* @__PURE__ */ jsxs("div", { className: "ds-approval__field", children: [
            /* @__PURE__ */ jsx("span", { className: "ds-approval__label", children: "\u0417\u0430\u0442\u0440\u0430\u0433\u0438\u0432\u0430\u0435\u043C\u044B\u0435 \u0440\u0435\u0441\u0443\u0440\u0441\u044B" }),
            renderAffectedResources ? renderAffectedResources(request) : /* @__PURE__ */ jsxs("ul", { className: "ds-approval__resources", children: [
              request.affectedResources.slice(0, 50).map((r, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: stable list ordering
                /* @__PURE__ */ jsx("li", { children: r }, `${r}-${i}`)
              )),
              request.affectedResources.length > 50 && /* @__PURE__ */ jsxs("li", { children: [
                "\u2026 \u0438 \u0435\u0449\u0451 ",
                request.affectedResources.length - 50
              ] })
            ] })
          ] }),
          request.estimatedCost && /* @__PURE__ */ jsxs("div", { className: "ds-approval__field", children: [
            /* @__PURE__ */ jsx("span", { className: "ds-approval__label", children: "\u041E\u0446\u0435\u043D\u043E\u0447\u043D\u0430\u044F \u0441\u0442\u043E\u0438\u043C\u043E\u0441\u0442\u044C" }),
            /* @__PURE__ */ jsx("span", { children: request.estimatedCost })
          ] }),
          denyMode ? /* @__PURE__ */ jsx(
            "textarea",
            {
              value: denyReason,
              onChange: (e) => setDenyReason(e.target.value),
              placeholder: labels?.reasonPlaceholder ?? "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043F\u0440\u0438\u0447\u0438\u043D\u0443 \u043E\u0442\u043A\u043B\u043E\u043D\u0435\u043D\u0438\u044F\u2026",
              className: "ds-approval__textarea",
              maxLength: 4e3,
              "aria-required": "true"
            }
          ) : /* @__PURE__ */ jsx(
            "textarea",
            {
              value: comment,
              onChange: (e) => setComment(e.target.value),
              placeholder: labels?.commentPlaceholder ?? "\u041A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0439 (\u043E\u043F\u0446\u0438\u043E\u043D\u0430\u043B\u044C\u043D\u043E)\u2026",
              className: "ds-approval__textarea",
              maxLength: 4e3
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("footer", { className: "ds-approval__footer", children: [
          closeable && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: onDismiss, disabled: disabled || !!submitting, children: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" }),
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "ghost",
              iconLeft: /* @__PURE__ */ jsx(X, {}),
              onClick: handleDeny,
              disabled: disabled || expired || denyMode && !denyReason.trim(),
              loading: submitting === "deny",
              children: denyLabel
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "primary",
              iconLeft: /* @__PURE__ */ jsx(Check, {}),
              onClick: handleApprove,
              disabled: disabled || expired || denyMode,
              loading: submitting === "approve",
              children: approveLabel
            }
          )
        ] })
      ]
    }
  );
  if (mode === "modal") {
    return /* @__PURE__ */ jsx("div", { className: "ds-approval__backdrop", role: "presentation", children: body });
  }
  return body;
}
function WorkspaceSwitcher({
  workspaces,
  currentWorkspaceId,
  recentWorkspaceIds = [],
  onSelect,
  onCreate,
  onManage,
  loading = false,
  onSearch,
  disabled = false,
  showArchived = false,
  "data-testid": testId
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [asyncResults, setAsyncResults] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const listId = useId();
  const current = workspaces.find((w) => w.id === currentWorkspaceId);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery("");
      setAsyncResults(null);
    }
  }, [open]);
  useEffect(() => {
    if (!onSearch || !query) {
      setAsyncResults(null);
      return;
    }
    const t = setTimeout(() => {
      onSearch(query).then(setAsyncResults).catch(() => setAsyncResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query, onSearch]);
  const filtered = useMemo(() => {
    const source = asyncResults ?? workspaces;
    const visible = showArchived ? source : source.filter((w) => !w.isArchived);
    if (!query || asyncResults) return visible;
    const q = query.toLowerCase();
    return visible.filter(
      (w) => w.name.toLowerCase().includes(q) || w.slug.toLowerCase().includes(q)
    );
  }, [asyncResults, workspaces, showArchived, query]);
  const recents = useMemo(() => {
    const set = new Set(recentWorkspaceIds);
    return workspaces.filter((w) => set.has(w.id));
  }, [workspaces, recentWorkspaceIds]);
  const handleSelect = (id) => {
    onSelect(id);
    setOpen(false);
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: containerRef,
      "data-testid": testId,
      className: cn("ds-workspace-switcher", open && "ds-workspace-switcher--open"),
      children: [
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            className: "ds-workspace-switcher__trigger",
            "aria-haspopup": "listbox",
            "aria-expanded": open,
            "aria-controls": listId,
            disabled: disabled || workspaces.length === 0,
            onClick: () => setOpen((o) => !o),
            children: [
              /* @__PURE__ */ jsx(Folder, { size: 16, "aria-hidden": "true", className: "ds-workspace-switcher__trigger-icon" }),
              /* @__PURE__ */ jsx("span", { className: "ds-workspace-switcher__trigger-name", children: current?.name ?? "Workspace \u043D\u0435 \u0432\u044B\u0431\u0440\u0430\u043D" }),
              workspaces.length > 1 && /* @__PURE__ */ jsx(ChevronDown, { size: 14, "aria-hidden": "true", className: "ds-workspace-switcher__chevron" })
            ]
          }
        ),
        open && /* @__PURE__ */ jsxs("div", { className: "ds-workspace-switcher__panel", role: "dialog", "aria-label": "Workspaces", children: [
          /* @__PURE__ */ jsxs("div", { className: "ds-workspace-switcher__search", children: [
            /* @__PURE__ */ jsx(Search, { size: 14, "aria-hidden": "true" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                ref: inputRef,
                type: "search",
                role: "combobox",
                "aria-controls": listId,
                "aria-autocomplete": "list",
                placeholder: "\u041F\u043E\u0438\u0441\u043A workspaces\u2026",
                value: query,
                onChange: (e) => setQuery(e.target.value),
                className: "ds-workspace-switcher__search-input"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("ul", { id: listId, role: "listbox", className: "ds-workspace-switcher__list", children: [
            loading && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__skeleton" }),
              /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__skeleton" }),
              /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__skeleton" })
            ] }),
            !loading && recents.length > 0 && !query && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__group", children: "\u041D\u0435\u0434\u0430\u0432\u043D\u0438\u0435" }),
              recents.slice(0, 5).map((w) => /* @__PURE__ */ jsx(
                WorkspaceRow,
                {
                  workspace: w,
                  isCurrent: w.id === currentWorkspaceId,
                  onSelect: handleSelect
                },
                `r-${w.id}`
              )),
              /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__divider", "aria-hidden": "true" })
            ] }),
            !loading && filtered.length === 0 && /* @__PURE__ */ jsx("li", { className: "ds-workspace-switcher__empty", children: "\u041D\u0435\u0442 workspaces, \u0443\u0434\u043E\u0432\u043B\u0435\u0442\u0432\u043E\u0440\u044F\u044E\u0449\u0438\u0445 \u0437\u0430\u043F\u0440\u043E\u0441\u0443." }),
            !loading && filtered.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsxs("li", { className: "ds-workspace-switcher__group", children: [
                "\u0412\u0441\u0435 workspaces (",
                filtered.length,
                ")"
              ] }),
              filtered.map((w) => /* @__PURE__ */ jsx(
                WorkspaceRow,
                {
                  workspace: w,
                  isCurrent: w.id === currentWorkspaceId,
                  onSelect: handleSelect
                },
                w.id
              ))
            ] })
          ] }),
          /* @__PURE__ */ jsxs("footer", { className: "ds-workspace-switcher__footer", children: [
            onCreate && /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                className: "ds-workspace-switcher__cta",
                onClick: () => {
                  onCreate();
                  setOpen(false);
                },
                children: [
                  /* @__PURE__ */ jsx(Plus, { size: 14, "aria-hidden": "true" }),
                  " \u0421\u043E\u0437\u0434\u0430\u0442\u044C workspace"
                ]
              }
            ),
            onManage && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: "ds-workspace-switcher__cta-link",
                onClick: () => {
                  onManage();
                  setOpen(false);
                },
                children: "\u0423\u043F\u0440\u0430\u0432\u043B\u044F\u0442\u044C \u2192"
              }
            )
          ] })
        ] })
      ]
    }
  );
}
function WorkspaceRow({
  workspace,
  isCurrent,
  onSelect
}) {
  return /* @__PURE__ */ jsx("li", { role: "option", "aria-selected": isCurrent, children: /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      className: cn(
        "ds-workspace-switcher__row",
        isCurrent && "ds-workspace-switcher__row--current"
      ),
      onClick: () => !isCurrent && onSelect(workspace.id),
      disabled: isCurrent,
      children: [
        /* @__PURE__ */ jsx(Folder, { size: 14, "aria-hidden": "true" }),
        /* @__PURE__ */ jsx("span", { className: "ds-workspace-switcher__row-name", children: workspace.name }),
        workspace.permissions?.canEdit === false && /* @__PURE__ */ jsx(
          Lock,
          {
            size: 12,
            "aria-label": "\u0422\u043E\u043B\u044C\u043A\u043E \u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440",
            className: "ds-workspace-switcher__row-lock"
          }
        ),
        isCurrent && /* @__PURE__ */ jsx(Check, { size: 14, "aria-hidden": "true", className: "ds-workspace-switcher__row-check" })
      ]
    }
  ) });
}
function flatDocs(nodes) {
  const out = [];
  const walk = (n) => {
    if (n.kind === "document") out.push(n);
    n.children?.forEach(walk);
  };
  nodes.forEach(walk);
  return out;
}
function findById(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children) {
      const found = findById(n.children, id);
      if (found) return found;
    }
  }
  return void 0;
}
function DocumentPicker({
  tree,
  selectedIds,
  maxSelection,
  acceptMimeTypes,
  onChange,
  onUpload,
  onAttachUrl,
  onCancel,
  onConfirm,
  mode = "modal",
  loading = false,
  "data-testid": testId
}) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(/* @__PURE__ */ new Set());
  const [urlValue, setUrlValue] = useState("");
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allDocs = useMemo(() => flatDocs(tree), [tree]);
  const matches = useMemo(() => {
    if (!query) return null;
    const q = query.toLowerCase();
    return allDocs.filter((d) => d.name.toLowerCase().includes(q));
  }, [allDocs, query]);
  const selectedDocs = useMemo(
    () => selectedIds.map((id) => findById(tree, id)).filter(Boolean),
    [selectedIds, tree]
  );
  const isAtMax = maxSelection != null && selectedIds.length >= maxSelection ? true : false;
  const toggle = (doc) => {
    if (!doc.canRead) return;
    const next = new Set(selectedSet);
    if (next.has(doc.id)) {
      next.delete(doc.id);
    } else {
      if (isAtMax) return;
      next.add(doc.id);
    }
    onChange(Array.from(next));
  };
  const toggleFolder = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };
  const handleUpload = async (e) => {
    if (!onUpload || !e.target.files) return;
    const added = await onUpload(e.target.files);
    if (added.length > 0) {
      onChange([...selectedIds, ...added.map((d) => d.id)]);
    }
  };
  const handleAttachUrl = async () => {
    if (!onAttachUrl || !urlValue) return;
    try {
      const node = await onAttachUrl(urlValue);
      setUrlValue("");
      onChange([...selectedIds, node.id]);
    } catch {
    }
  };
  const renderRow = (doc, depth) => {
    if (doc.kind === "folder") {
      const isOpen = expanded.has(doc.id);
      return /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "ds-doc-picker__row ds-doc-picker__row--folder",
            style: { paddingLeft: `${depth * 16 + 8}px` },
            children: [
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "ds-doc-picker__chev",
                  onClick: () => toggleFolder(doc.id),
                  "aria-expanded": isOpen,
                  "aria-label": isOpen ? "\u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C" : "\u0420\u0430\u0437\u0432\u0435\u0440\u043D\u0443\u0442\u044C",
                  children: isOpen ? /* @__PURE__ */ jsx(ChevronDown, { size: 14 }) : /* @__PURE__ */ jsx(ChevronRight, { size: 14 })
                }
              ),
              isOpen ? /* @__PURE__ */ jsx(FolderOpen, { size: 14 }) : /* @__PURE__ */ jsx(Folder, { size: 14 }),
              /* @__PURE__ */ jsx("span", { className: "ds-doc-picker__row-name", children: doc.name })
            ]
          }
        ),
        isOpen && doc.children?.map((child) => renderRow(child, depth + 1))
      ] }, doc.id);
    }
    const checked = selectedSet.has(doc.id);
    const denied = !doc.canRead;
    const wrongType = Boolean(
      acceptMimeTypes && doc.mimeType && !acceptMimeTypes.includes(doc.mimeType)
    );
    const disabled = denied || !checked && (isAtMax || wrongType);
    return /* @__PURE__ */ jsxs(
      "label",
      {
        className: cn(
          "ds-doc-picker__row",
          disabled && "ds-doc-picker__row--disabled",
          denied && "ds-doc-picker__row--denied"
        ),
        style: { paddingLeft: `${depth * 16 + 30}px` },
        title: denied ? doc.denialReason ?? "\u041D\u0435\u0442 \u0434\u043E\u0441\u0442\u0443\u043F\u0430" : wrongType ? "\u041D\u0435\u043F\u043E\u0434\u0445\u043E\u0434\u044F\u0449\u0438\u0439 \u0442\u0438\u043F \u0444\u0430\u0439\u043B\u0430" : isAtMax && !checked ? `\u041C\u0430\u043A\u0441\u0438\u043C\u0443\u043C ${maxSelection}` : void 0,
        children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "checkbox",
              checked,
              onChange: () => toggle(doc),
              disabled,
              "aria-label": `\u0412\u044B\u0431\u0440\u0430\u0442\u044C ${doc.name}`
            }
          ),
          denied ? /* @__PURE__ */ jsx(Lock, { size: 14 }) : /* @__PURE__ */ jsx(FileText, { size: 14 }),
          /* @__PURE__ */ jsx("span", { className: "ds-doc-picker__row-name", children: doc.name }),
          doc.lastModifiedAt && /* @__PURE__ */ jsx("span", { className: "ds-doc-picker__row-meta", children: doc.lastModifiedAt })
        ]
      },
      doc.id
    );
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: mode === "modal" ? "dialog" : "region",
      "aria-label": "\u0412\u044B\u0431\u043E\u0440 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u0432",
      "data-testid": testId,
      className: cn("ds-doc-picker", `ds-doc-picker--${mode}`),
      children: [
        /* @__PURE__ */ jsxs("header", { className: "ds-doc-picker__header", children: [
          /* @__PURE__ */ jsx("h3", { className: "ds-doc-picker__title", children: "\u041F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u044B" }),
          onCancel && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "ds-doc-picker__close",
              "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C",
              onClick: onCancel,
              children: /* @__PURE__ */ jsx(X, { size: 16 })
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "ds-doc-picker__toolbar", children: [
          /* @__PURE__ */ jsxs("div", { className: "ds-doc-picker__search", children: [
            /* @__PURE__ */ jsx(Search, { size: 14, "aria-hidden": "true" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "search",
                placeholder: "\u041F\u043E\u0438\u0441\u043A \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u0432\u2026",
                value: query,
                onChange: (e) => setQuery(e.target.value)
              }
            )
          ] }),
          onUpload && /* @__PURE__ */ jsxs("label", { className: "ds-doc-picker__upload", children: [
            /* @__PURE__ */ jsx(Upload, { size: 14, "aria-hidden": "true" }),
            "\u0417\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C",
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "file",
                multiple: true,
                accept: acceptMimeTypes?.join(","),
                onChange: handleUpload,
                hidden: true
              }
            )
          ] })
        ] }),
        selectedDocs.length > 0 && /* @__PURE__ */ jsxs("div", { className: "ds-doc-picker__chips", children: [
          /* @__PURE__ */ jsxs("span", { className: "ds-doc-picker__label", children: [
            "\u0412\u044B\u0431\u0440\u0430\u043D\u043E (",
            selectedDocs.length,
            "):"
          ] }),
          selectedDocs.map((d) => /* @__PURE__ */ jsxs("span", { className: "ds-doc-picker__chip", children: [
            /* @__PURE__ */ jsx(FileText, { size: 12, "aria-hidden": "true" }),
            d.name,
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => onChange(selectedIds.filter((id) => id !== d.id)),
                "aria-label": `\u0423\u0431\u0440\u0430\u0442\u044C ${d.name}`,
                className: "ds-doc-picker__chip-remove",
                children: /* @__PURE__ */ jsx(X, { size: 12 })
              }
            )
          ] }, d.id))
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "ds-doc-picker__tree", role: "tree", children: [
          loading && /* @__PURE__ */ jsx("div", { className: "ds-doc-picker__skeleton", children: "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u0434\u0435\u0440\u0435\u0432\u043E\u2026" }),
          !loading && matches != null && matches.length === 0 && /* @__PURE__ */ jsx("p", { className: "ds-doc-picker__empty", children: "\u0421\u043E\u0432\u043F\u0430\u0434\u0435\u043D\u0438\u0439 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E." }),
          !loading && matches != null && matches.length > 0 && matches.map((d) => renderRow(d, 0)),
          !loading && matches == null && tree.length === 0 && /* @__PURE__ */ jsx("p", { className: "ds-doc-picker__empty", children: "\u0412 \u044D\u0442\u043E\u043C workspace \u043F\u043E\u043A\u0430 \u043D\u0435\u0442 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u0432." }),
          !loading && matches == null && tree.length > 0 && tree.map((d) => renderRow(d, 0))
        ] }),
        onAttachUrl && /* @__PURE__ */ jsxs("div", { className: "ds-doc-picker__url", children: [
          /* @__PURE__ */ jsx("span", { className: "ds-doc-picker__label", children: "\u0418\u043B\u0438 \u043F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C \u043F\u043E URL:" }),
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "url",
              value: urlValue,
              onChange: (e) => setUrlValue(e.target.value),
              placeholder: "https://\u2026"
            }
          ),
          /* @__PURE__ */ jsx(Button, { size: "sm", variant: "secondary", onClick: handleAttachUrl, disabled: !urlValue, children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C" })
        ] }),
        /* @__PURE__ */ jsxs("footer", { className: "ds-doc-picker__footer", children: [
          onCancel && /* @__PURE__ */ jsx(Button, { variant: "ghost", onClick: onCancel, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }),
          onConfirm && /* @__PURE__ */ jsxs(Button, { variant: "primary", onClick: () => onConfirm(selectedIds), children: [
            "\u041F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C (",
            selectedIds.length,
            ")"
          ] })
        ] })
      ]
    }
  );
}
var ROLE_ICON = {
  assistant: Bot,
  user: User,
  system: Settings2,
  "tool-call": Wrench,
  "tool-result": Wrench,
  error: AlertCircle
};
function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString(void 0, {
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "";
  }
}
function MessageBubble({
  role,
  author,
  timestamp,
  toolName,
  className,
  children,
  ...rest
}) {
  const Icon = ROLE_ICON[role];
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "article",
      className: cn("ds-msg", `ds-msg--${role}`, className),
      "data-role": role,
      ...rest,
      children: [
        /* @__PURE__ */ jsx("div", { className: "ds-msg__avatar", "aria-hidden": "true", children: Icon ? /* @__PURE__ */ jsx(Icon, { size: 16 }) : null }),
        /* @__PURE__ */ jsxs("div", { className: "ds-msg__body", children: [
          /* @__PURE__ */ jsxs("div", { className: "ds-msg__head", children: [
            /* @__PURE__ */ jsxs("span", { className: "ds-msg__author", children: [
              author ?? defaultAuthor(role),
              toolName && /* @__PURE__ */ jsx("code", { className: "ds-msg__tool", "aria-label": `\u0418\u043D\u0441\u0442\u0440\u0443\u043C\u0435\u043D\u0442: ${toolName}`, children: toolName })
            ] }),
            timestamp && /* @__PURE__ */ jsx("time", { className: "ds-msg__time", children: formatTime(timestamp) })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "ds-msg__content", children })
        ] })
      ]
    }
  );
}
function defaultAuthor(role) {
  switch (role) {
    case "assistant":
      return "\u0410\u0433\u0435\u043D\u0442";
    case "user":
      return "\u0412\u044B";
    case "system":
      return "\u0421\u0438\u0441\u0442\u0435\u043C\u0430";
    case "tool-call":
    case "tool-result":
      return "\u0418\u043D\u0441\u0442\u0440\u0443\u043C\u0435\u043D\u0442";
    case "error":
      return "\u041E\u0448\u0438\u0431\u043A\u0430";
  }
}
var MODE_LABELS = {
  plan: "\u041F\u043B\u0430\u043D",
  auto: "\u0410\u0432\u0442\u043E",
  confirm: "\u041F\u043E\u0434\u0442\u0432.",
  research: "\u0418\u0441\u0441\u043B\u0435\u0434.",
  chat: "\u0427\u0430\u0442"
};
var SURFACE_LABELS = {
  chat: "Chat",
  cowork: "Cowork",
  code: "Code"
};
function Composer({
  value,
  defaultValue,
  onChange,
  onSubmit,
  onAttach,
  onVoice,
  placeholder = "\u041E\u043F\u0438\u0448\u0438\u0442\u0435 \u0437\u0430\u0434\u0430\u0447\u0443 \u0438\u043B\u0438 \u0437\u0430\u0434\u0430\u0439\u0442\u0435 \u0432\u043E\u043F\u0440\u043E\u0441\u2026",
  disabled = false,
  busy = false,
  onStop,
  mode = "auto",
  onModeChange,
  modes = ["plan", "auto", "confirm", "research", "chat"],
  surfaceMode = "chat",
  onSurfaceModeChange,
  slashSkills = []
}) {
  const [internal, setInternal] = useState(defaultValue ?? "");
  const taRef = useRef(null);
  const isControlled = value !== void 0;
  const text = isControlled ? value : internal;
  const setText = (v) => {
    const ta = taRef.current;
    let restore = null;
    if (ta && document.activeElement === ta) {
      restore = {
        start: ta.selectionStart ?? v.length,
        end: ta.selectionEnd ?? v.length
      };
    }
    if (!isControlled) setInternal(v);
    onChange?.(v);
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = `${Math.min(ta.scrollHeight, 192)}px`;
      if (restore && document.activeElement === ta) {
        const max = v.length;
        ta.setSelectionRange(
          Math.min(restore.start, max),
          Math.min(restore.end, max)
        );
      }
    }
  };
  const submit = async () => {
    if (!text.trim() || busy || disabled) return;
    await onSubmit(text);
    if (!isControlled) setInternal("");
    if (taRef.current) taRef.current.style.height = "auto";
  };
  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };
  const slashState = useMemo(() => {
    const lines = text.split("\n");
    const last = lines[lines.length - 1] ?? "";
    const m = last.match(/^\/([\w-]*)$/);
    if (!m || slashSkills.length === 0) return { open: false, filter: "", lastLine: last };
    const filter = (m[1] ?? "").toLowerCase();
    return { open: true, filter, lastLine: last };
  }, [text, slashSkills]);
  const filteredSlash = useMemo(() => {
    if (!slashState.open) return [];
    const f = slashState.filter;
    return slashSkills.filter(
      (s) => f === "" || s.name.toLowerCase().startsWith(f) || s.name.toLowerCase().includes(f)
    ).slice(0, 8);
  }, [slashSkills, slashState]);
  const insertSlashSkill = (name) => {
    const lines = text.split("\n");
    lines[lines.length - 1] = `/${name} `;
    const next = lines.join("\n");
    setText(next);
    requestAnimationFrame(() => taRef.current?.focus());
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("ds-composer", busy && "ds-composer--busy"), children: [
    onSurfaceModeChange && /* @__PURE__ */ jsx(
      "div",
      {
        className: "ds-composer__modes",
        role: "radiogroup",
        "aria-label": "\u0420\u0435\u0436\u0438\u043C \u0440\u0430\u0431\u043E\u0447\u0435\u0439 \u043E\u0431\u043B\u0430\u0441\u0442\u0438",
        children: ["chat", "cowork", "code"].map((m) => /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            role: "radio",
            "aria-checked": m === surfaceMode,
            className: cn(
              "ds-composer__mode-pill",
              m === surfaceMode && "ds-composer__mode-pill--active"
            ),
            onClick: () => onSurfaceModeChange(m),
            children: SURFACE_LABELS[m]
          },
          m
        ))
      }
    ),
    onModeChange && /* @__PURE__ */ jsx("div", { className: "ds-composer__modes", role: "radiogroup", "aria-label": "\u0420\u0435\u0436\u0438\u043C \u0430\u0433\u0435\u043D\u0442\u0430", children: modes.map((m) => /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        role: "radio",
        "aria-checked": m === mode,
        className: cn(
          "ds-composer__mode-pill",
          m === mode && "ds-composer__mode-pill--active"
        ),
        onClick: () => onModeChange(m),
        children: MODE_LABELS[m]
      },
      m
    )) }),
    /* @__PURE__ */ jsxs("div", { className: "ds-composer__row", style: { position: "relative" }, children: [
      onAttach && /* @__PURE__ */ jsx(
        IconButton,
        {
          "aria-label": "\u041F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C \u0444\u0430\u0439\u043B",
          icon: /* @__PURE__ */ jsx(Paperclip, {}),
          onClick: onAttach,
          size: "md",
          variant: "ghost"
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "ds-composer__field", children: [
        slashState.open && filteredSlash.length > 0 && /* @__PURE__ */ jsx(
          "div",
          {
            className: "ds-composer__slash-menu",
            role: "listbox",
            "aria-label": "Skills",
            children: filteredSlash.map((s) => /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                role: "option",
                className: "ds-composer__slash-item",
                onMouseDown: (ev) => {
                  ev.preventDefault();
                  insertSlashSkill(s.name);
                },
                children: [
                  /* @__PURE__ */ jsxs("span", { className: "ds-composer__slash-name", children: [
                    "/",
                    s.name
                  ] }),
                  s.description ? /* @__PURE__ */ jsx("span", { className: "ds-composer__slash-desc", children: s.description }) : null
                ]
              },
              s.name
            ))
          }
        ),
        /* @__PURE__ */ jsx(
          "textarea",
          {
            ref: taRef,
            value: text,
            onChange: (e) => setText(e.target.value),
            onKeyDown: handleKey,
            placeholder,
            disabled,
            rows: 1,
            className: "ds-composer__textarea",
            "aria-label": "\u0421\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0435"
          }
        )
      ] }),
      onVoice && /* @__PURE__ */ jsx(
        IconButton,
        {
          "aria-label": "\u0413\u043E\u043B\u043E\u0441\u043E\u0432\u043E\u0439 \u0432\u0432\u043E\u0434",
          icon: /* @__PURE__ */ jsx(Mic, {}),
          onClick: onVoice,
          size: "md",
          variant: "ghost"
        }
      ),
      busy && onStop ? /* @__PURE__ */ jsx(
        IconButton,
        {
          "aria-label": "\u041E\u0441\u0442\u0430\u043D\u043E\u0432\u0438\u0442\u044C",
          icon: /* @__PURE__ */ jsx(Square, { size: 14, fill: "currentColor" }),
          onClick: () => void Promise.resolve(onStop()),
          size: "md",
          variant: "primary",
          className: "ds-composer__stop-btn"
        }
      ) : /* @__PURE__ */ jsx(
        IconButton,
        {
          "aria-label": "\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C",
          icon: /* @__PURE__ */ jsx(Send, {}),
          onClick: submit,
          disabled: !text.trim() || disabled || busy,
          size: "md",
          variant: "primary"
        }
      )
    ] })
  ] });
}
function ChatTabs({ tabs, activeId, onSelect, onClose, onNew }) {
  return /* @__PURE__ */ jsxs("div", { role: "tablist", "aria-label": "\u0427\u0430\u0442\u044B", className: "ds-chat-tabs", children: [
    tabs.map((tab) => /* @__PURE__ */ jsxs(
      "div",
      {
        role: "tab",
        "aria-selected": tab.id === activeId,
        "aria-controls": `panel-${tab.id}`,
        id: `tab-${tab.id}`,
        className: cn("ds-chat-tabs__tab", tab.id === activeId && "ds-chat-tabs__tab--active"),
        children: [
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              className: "ds-chat-tabs__title",
              onClick: () => onSelect(tab.id),
              tabIndex: tab.id === activeId ? 0 : -1,
              children: [
                tab.title,
                tab.unread != null && tab.unread > 0 && /* @__PURE__ */ jsx("span", { className: "ds-chat-tabs__unread", children: tab.unread })
              ]
            }
          ),
          onClose && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "aria-label": `\u0417\u0430\u043A\u0440\u044B\u0442\u044C ${tab.title}`,
              onClick: () => onClose(tab.id),
              className: "ds-chat-tabs__close",
              children: /* @__PURE__ */ jsx(X, { size: 12 })
            }
          )
        ]
      },
      tab.id
    )),
    onNew && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "aria-label": "\u041D\u043E\u0432\u044B\u0439 \u0447\u0430\u0442",
        onClick: onNew,
        className: "ds-chat-tabs__new",
        children: /* @__PURE__ */ jsx(Plus, { size: 14 })
      }
    )
  ] });
}
function AgentProgressCard({
  status,
  currentStep,
  progress,
  counter,
  detail
}) {
  return /* @__PURE__ */ jsxs("div", { className: cn("ds-agent-progress", `ds-agent-progress--${status}`), children: [
    /* @__PURE__ */ jsxs("div", { className: "ds-agent-progress__top", children: [
      /* @__PURE__ */ jsxs("span", { className: "ds-agent-progress__indicator", "aria-hidden": "true", children: [
        status === "running" && /* @__PURE__ */ jsx(Loader2, { className: "ds-agent-progress__spin", size: 16 }),
        status === "success" && /* @__PURE__ */ jsx(CheckCircle2, { size: 16 }),
        status === "failed" && /* @__PURE__ */ jsx(AlertCircle, { size: 16 })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "ds-agent-progress__main", children: [
        currentStep && /* @__PURE__ */ jsx("p", { className: "ds-agent-progress__step", children: currentStep }),
        counter && /* @__PURE__ */ jsx("p", { className: "ds-agent-progress__counter", children: counter })
      ] })
    ] }),
    progress != null && /* @__PURE__ */ jsx("div", { className: "ds-agent-progress__bar", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
      "span",
      {
        className: "ds-agent-progress__bar-fill",
        style: { width: `${Math.round(Math.min(1, Math.max(0, progress)) * 100)}%` }
      }
    ) }),
    detail && /* @__PURE__ */ jsx("div", { className: "ds-agent-progress__detail", children: detail })
  ] });
}
function formatDuration2(ms) {
  if (ms < 1e3) return `${ms}ms`;
  if (ms < 6e4) return `${(ms / 1e3).toFixed(1)}s`;
  const minutes = Math.floor(ms / 6e4);
  const seconds = Math.round(ms % 6e4 / 1e3);
  return `${minutes}m ${seconds}s`;
}
function ThinkingBlock({
  children,
  isActive = false,
  tokenCount,
  durationMs,
  defaultOpen = false,
  preview,
  labelActive = "\u0420\u0430\u0437\u043C\u044B\u0448\u043B\u044F\u0435\u0442\u2026",
  labelDone = "\u0420\u0430\u0437\u043C\u044B\u0448\u043B\u0435\u043D\u0438\u0435",
  className
}) {
  const [open, setOpen] = useState(defaultOpen);
  const previewText = !open && !isActive ? (preview ?? "").trim() : "";
  const onKey = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen((o) => !o);
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "ds-thinking",
        isActive && "ds-thinking--active",
        open && "ds-thinking--open",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "ds-thinking__header",
            onClick: () => setOpen((o) => !o),
            onKeyDown: onKey,
            role: "button",
            tabIndex: 0,
            "aria-expanded": open,
            children: [
              /* @__PURE__ */ jsx("span", { className: cn("ds-thinking__icon", isActive && "ds-thinking__icon--active"), children: isActive ? "\u25D0" : "\u2234" }),
              /* @__PURE__ */ jsx("span", { className: "ds-thinking__label", children: isActive ? labelActive : labelDone }),
              !isActive && durationMs != null && durationMs > 0 && /* @__PURE__ */ jsx("span", { className: "ds-thinking__duration", title: "\u0412\u0440\u0435\u043C\u044F \u0440\u0430\u0437\u043C\u044B\u0448\u043B\u0435\u043D\u0438\u044F", children: formatDuration2(durationMs) }),
              !isActive && tokenCount != null && tokenCount > 0 && /* @__PURE__ */ jsxs("span", { className: "ds-thinking__tokens", children: [
                "~",
                tokenCount,
                "t"
              ] }),
              previewText && /* @__PURE__ */ jsxs("span", { className: "ds-thinking__preview", "aria-hidden": "true", children: [
                "\u2014 ",
                previewText
              ] }),
              /* @__PURE__ */ jsx("span", { className: "ds-thinking__chevron", "aria-hidden": "true", children: "\u25B8" })
            ]
          }
        ),
        open && children && /* @__PURE__ */ jsx("div", { className: "ds-thinking__body", children })
      ]
    }
  );
}
function StatusBadge({
  kind,
  label,
  ariaLabel,
  size = "md",
  variant = "pill",
  title,
  className
}) {
  const a11y = ariaLabel ?? label ?? kind;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "ds-status-badge",
        `ds-status-badge--${kind}`,
        `ds-status-badge--${size}`,
        `ds-status-badge--${variant}`,
        className
      ),
      role: "status",
      "aria-label": a11y,
      title: title ?? a11y,
      children: [
        /* @__PURE__ */ jsx(StatusIcon, { kind }),
        label && variant !== "dot" && /* @__PURE__ */ jsx("span", { className: "ds-status-badge__label", children: label })
      ]
    }
  );
}
function StatusIcon({ kind }) {
  const base = {
    width: 12,
    height: 12,
    viewBox: "0 0 16 16",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: false
  };
  switch (kind) {
    case "success":
      return /* @__PURE__ */ jsx("svg", { ...base, className: "ds-status-badge__icon", children: /* @__PURE__ */ jsx("polyline", { points: "3 8 7 12 13 4" }) });
    case "warning":
      return /* @__PURE__ */ jsxs("svg", { ...base, className: "ds-status-badge__icon", children: [
        /* @__PURE__ */ jsx("path", { d: "M8 2 L14 13 L2 13 Z" }),
        /* @__PURE__ */ jsx("line", { x1: "8", y1: "6", x2: "8", y2: "9" }),
        /* @__PURE__ */ jsx("circle", { cx: "8", cy: "11.5", r: "0.5", fill: "currentColor" })
      ] });
    case "danger":
      return /* @__PURE__ */ jsxs("svg", { ...base, className: "ds-status-badge__icon", children: [
        /* @__PURE__ */ jsx("line", { x1: "4", y1: "4", x2: "12", y2: "12" }),
        /* @__PURE__ */ jsx("line", { x1: "12", y1: "4", x2: "4", y2: "12" })
      ] });
    case "info":
      return /* @__PURE__ */ jsxs("svg", { ...base, className: "ds-status-badge__icon", children: [
        /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
        /* @__PURE__ */ jsx("line", { x1: "8", y1: "7", x2: "8", y2: "11" }),
        /* @__PURE__ */ jsx("circle", { cx: "8", cy: "5", r: "0.5", fill: "currentColor" })
      ] });
    case "running":
      return /* @__PURE__ */ jsx("svg", { ...base, className: "ds-status-badge__icon ds-status-badge__icon--spin", children: /* @__PURE__ */ jsx("path", { d: "M8 2 A6 6 0 1 1 2 8" }) });
    case "idle":
      return /* @__PURE__ */ jsx("svg", { ...base, className: "ds-status-badge__icon", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "3", fill: "currentColor", stroke: "none" }) });
  }
}
function formatElapsed(ms) {
  const seconds = Math.floor(ms / 1e3);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${remaining}s`;
}
function SpinnerWithModes({
  mode,
  theme = "default",
  elapsedMs,
  label,
  size = "md",
  className
}) {
  const visual = useMemo(() => {
    switch (mode) {
      case "idle":
        return null;
      case "thinking":
        return /* @__PURE__ */ jsxs("div", { className: "ds-spinner__thinking", children: [
          /* @__PURE__ */ jsx("div", { className: "ds-spinner__thinking-ring" }),
          /* @__PURE__ */ jsx("div", { className: "ds-spinner__thinking-ring" }),
          /* @__PURE__ */ jsx("div", { className: "ds-spinner__thinking-ring" })
        ] });
      case "tool":
        return /* @__PURE__ */ jsxs("svg", { className: "ds-spinner__tool", viewBox: "0 0 20 20", fill: "none", children: [
          /* @__PURE__ */ jsx("title", { children: "tool running" }),
          /* @__PURE__ */ jsx(
            "path",
            {
              d: "M10 2a1 1 0 011 1v1.07A6.003 6.003 0 0114.93 8H16a1 1 0 110 2h-1.07A6.003 6.003 0 0111 13.93V15a1 1 0 11-2 0v-1.07A6.003 6.003 0 015.07 10H4a1 1 0 110-2h1.07A6.003 6.003 0 019 4.07V3a1 1 0 011-1zm0 4a4 4 0 100 8 4 4 0 000-8z",
              fill: "currentColor"
            }
          )
        ] });
      case "busy":
        return /* @__PURE__ */ jsxs("div", { className: "ds-spinner__busy", children: [
          /* @__PURE__ */ jsx("span", { className: "ds-spinner__busy-dot" }),
          /* @__PURE__ */ jsx("span", { className: "ds-spinner__busy-dot" }),
          /* @__PURE__ */ jsx("span", { className: "ds-spinner__busy-dot" })
        ] });
      case "waiting":
        return /* @__PURE__ */ jsx("div", { className: "ds-spinner__waiting" });
      case "compiling":
        return /* @__PURE__ */ jsxs("svg", { className: "ds-spinner__compiling", viewBox: "0 0 20 20", children: [
          /* @__PURE__ */ jsx("title", { children: "compiling" }),
          /* @__PURE__ */ jsx("circle", { className: "ds-spinner__compiling-circle", cx: "10", cy: "10", r: "7" })
        ] });
    }
  }, [mode]);
  if (mode === "idle") return null;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "ds-spinner",
        size !== "md" && `ds-spinner--${size}`,
        theme !== "default" && `ds-spinner--${theme}`,
        className
      ),
      role: "status",
      "aria-label": label || `${mode}\u2026`,
      children: [
        /* @__PURE__ */ jsx("span", { className: "ds-spinner__visual", children: visual }),
        label && /* @__PURE__ */ jsx("span", { className: "ds-spinner__label", children: label }),
        elapsedMs != null && elapsedMs > 0 && /* @__PURE__ */ jsx("span", { className: "ds-spinner__elapsed", children: formatElapsed(elapsedMs) })
      ]
    }
  );
}
var TOOL_ICONS = {
  read_file: FileText,
  read: FileText,
  write: Edit3,
  edit: Edit3,
  patch: Edit3,
  apply_patch: Edit3,
  bash: TerminalSquare,
  shell: TerminalSquare,
  glob: Search,
  grep: Search,
  search: Search,
  fetch: Globe,
  web_search: Globe,
  code: Code2
};
var DIFF_TOOLS = /* @__PURE__ */ new Set(["edit", "write", "patch", "apply_patch"]);
function toolKey(name) {
  return name.toLowerCase().replace(/[^a-z_]/g, "");
}
function getToolIcon(name) {
  return TOOL_ICONS[toolKey(name)] ?? Hammer;
}
function formatDuration3(ms) {
  if (ms < 1e3) return `${ms}ms`;
  return `${(ms / 1e3).toFixed(1)}s`;
}
var DIFF_PATTERN_HEAD = /^[+-]{3} /m;
var DIFF_PATTERN_HUNK = /^@@/m;
function detectDiff(text) {
  return DIFF_PATTERN_HEAD.test(text) && DIFF_PATTERN_HUNK.test(text);
}
function DiffOutput({ text }) {
  const lines = useMemo(() => text.split("\n"), [text]);
  return /* @__PURE__ */ jsx("div", { className: "ds-tool__diff", children: lines.map((line, i) => {
    const prefix = line[0];
    const isAdd = prefix === "+" && !line.startsWith("+++");
    const isDel = prefix === "-" && !line.startsWith("---");
    const isHunk = line.startsWith("@@");
    const cls = isAdd ? "ds-tool__diff-line--add" : isDel ? "ds-tool__diff-line--del" : isHunk ? "ds-tool__diff-line--hunk" : "ds-tool__diff-line--ctx";
    return /* @__PURE__ */ jsxs("div", { className: cn("ds-tool__diff-line", cls), children: [
      /* @__PURE__ */ jsx("span", { className: "ds-tool__diff-gutter", children: prefix ?? " " }),
      /* @__PURE__ */ jsx("span", { className: "ds-tool__diff-text", children: isAdd || isDel ? line.slice(1) : line })
    ] }, `${i}-${line.slice(0, 8)}`);
  }) });
}
function TruncatedOutput({
  text,
  diff,
  truncateLines,
  outputNode
}) {
  const [expanded, setExpanded] = useState(false);
  const lines = useMemo(() => text.split("\n"), [text]);
  const overflows = lines.length > truncateLines;
  const showAll = !overflows || expanded;
  const visible = showAll ? text : lines.slice(0, truncateLines).join("\n");
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    diff ? /* @__PURE__ */ jsx(DiffOutput, { text: visible }) : outputNode && showAll ? /* @__PURE__ */ jsx("div", { className: "ds-tool__output", children: outputNode }) : /* @__PURE__ */ jsx("pre", { className: "ds-tool__pre", children: visible }),
    overflows && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "ds-tool__truncate",
        onClick: () => setExpanded((v) => !v),
        children: expanded ? `\u25B2 \u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C (${lines.length} \u0441\u0442\u0440\u043E\u043A)` : `\u25BC \u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u0432\u0441\u0435 ${lines.length} \u0441\u0442\u0440\u043E\u043A`
      }
    )
  ] });
}
function ToolUseCard({
  name,
  summary,
  detail,
  status,
  input,
  output,
  outputNode,
  defaultExpanded = false,
  durationMs,
  truncateLines = 40,
  isDiff,
  className
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const headerId = useId();
  const Icon = getToolIcon(name);
  const showDiff = output != null && (isDiff ?? (DIFF_TOOLS.has(toolKey(name)) && detectDiff(output)));
  const onKey = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setExpanded((e) => !e);
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "ds-tool",
        `ds-tool--${status}`,
        expanded && "ds-tool--expanded",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            id: headerId,
            className: "ds-tool__header",
            onClick: () => setExpanded((e) => !e),
            onKeyDown: onKey,
            role: "button",
            tabIndex: 0,
            "aria-expanded": expanded,
            children: [
              /* @__PURE__ */ jsx("span", { className: "ds-tool__icon", "aria-hidden": "true", children: /* @__PURE__ */ jsx(Icon, { size: 14 }) }),
              /* @__PURE__ */ jsxs("span", { className: "ds-tool__title", children: [
                /* @__PURE__ */ jsx("span", { className: "ds-tool__name", children: summary ?? name }),
                summary && /* @__PURE__ */ jsx("span", { className: "ds-tool__detail", children: detail ?? name })
              ] }),
              /* @__PURE__ */ jsx(
                StatusBadge,
                {
                  kind: status === "done" ? "success" : status === "error" ? "danger" : "running",
                  ariaLabel: status === "done" ? "\u0412\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u043E" : status === "error" ? "\u041E\u0448\u0438\u0431\u043A\u0430" : "\u0412\u044B\u043F\u043E\u043B\u043D\u044F\u0435\u0442\u0441\u044F",
                  size: "sm",
                  variant: "inline"
                }
              ),
              durationMs != null && /* @__PURE__ */ jsx("span", { className: "ds-tool__duration", children: formatDuration3(durationMs) }),
              showDiff && /* @__PURE__ */ jsx("span", { className: "ds-tool__diff-badge", children: "diff" }),
              /* @__PURE__ */ jsx("span", { className: "ds-tool__chevron", "aria-hidden": "true", children: "\u25B8" })
            ]
          }
        ),
        expanded && /* @__PURE__ */ jsxs(Fragment, { children: [
          input && /* @__PURE__ */ jsxs("div", { className: "ds-tool__section", children: [
            /* @__PURE__ */ jsx("div", { className: "ds-tool__section-label", children: "\u0417\u0430\u043F\u0440\u043E\u0441" }),
            /* @__PURE__ */ jsx("pre", { className: "ds-tool__pre", children: input })
          ] }),
          (output || outputNode) && /* @__PURE__ */ jsxs("div", { className: "ds-tool__section", children: [
            /* @__PURE__ */ jsx("div", { className: "ds-tool__section-label", children: status === "error" ? "\u041E\u0448\u0438\u0431\u043A\u0430" : "\u0420\u0435\u0437\u0443\u043B\u044C\u0442\u0430\u0442" }),
            output != null ? /* @__PURE__ */ jsx(
              TruncatedOutput,
              {
                text: output,
                diff: Boolean(showDiff),
                truncateLines,
                outputNode
              }
            ) : /* @__PURE__ */ jsx("div", { className: "ds-tool__output", children: outputNode })
          ] })
        ] })
      ]
    }
  );
}
function defaultRender(content) {
  if (!content) return null;
  return /* @__PURE__ */ jsx("p", { className: "ds-stream__plain", children: content });
}
function StreamingMessage({
  content,
  isStreaming,
  render = defaultRender,
  className
}) {
  const [showFadingCursor, setShowFadingCursor] = useState(false);
  const wasStreamingRef = useRef(isStreaming);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && content.length > 0) {
      setShowFadingCursor(true);
      const timer = setTimeout(() => setShowFadingCursor(false), 300);
      wasStreamingRef.current = isStreaming;
      return () => clearTimeout(timer);
    }
    wasStreamingRef.current = isStreaming;
    return void 0;
  }, [isStreaming, content.length]);
  const { stableContent, tailContent } = useMemo(() => {
    if (!isStreaming) return { stableContent: content, tailContent: "" };
    const lastDoubleNewline = content.lastIndexOf("\n\n");
    if (lastDoubleNewline < 0) {
      return { stableContent: "", tailContent: content };
    }
    return {
      stableContent: content.slice(0, lastDoubleNewline),
      tailContent: content.slice(lastDoubleNewline)
    };
  }, [content, isStreaming]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn("ds-stream", isStreaming && "ds-stream--streaming", className),
      "aria-live": "polite",
      "aria-atomic": "false",
      children: [
        stableContent && /* @__PURE__ */ jsx("div", { className: "ds-stream__stable", children: render(stableContent, "stable") }),
        tailContent && /* @__PURE__ */ jsx("div", { className: "ds-stream__tail", children: render(tailContent, "tail") }),
        !isStreaming && !stableContent && !tailContent && content && /* @__PURE__ */ jsx("div", { className: "ds-stream__stable", children: render(content, "stable") }),
        isStreaming && !tailContent && /* @__PURE__ */ jsx("span", { className: "ds-stream__cursor", "aria-hidden": "true" }),
        showFadingCursor && !isStreaming && /* @__PURE__ */ jsx(
          "span",
          {
            className: "ds-stream__cursor ds-stream__cursor--fading",
            "aria-hidden": "true"
          }
        )
      ]
    }
  );
}
function MessageGroup({
  summary,
  badge,
  depth = 0,
  status,
  counter,
  defaultCollapsed = true,
  children,
  className
}) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const onKey = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen((o) => !o);
    }
  };
  const indent = Math.min(depth, 4) * 12;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "ds-msg-group",
        `ds-msg-group--depth-${Math.min(depth, 4)}`,
        status && `ds-msg-group--${status}`,
        open && "ds-msg-group--open",
        className
      ),
      style: depth > 0 ? { marginLeft: indent } : void 0,
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "ds-msg-group__header",
            onClick: () => setOpen((o) => !o),
            onKeyDown: onKey,
            role: "button",
            tabIndex: 0,
            "aria-expanded": open,
            children: [
              /* @__PURE__ */ jsx("span", { className: "ds-msg-group__chevron", "aria-hidden": "true", children: "\u25B8" }),
              /* @__PURE__ */ jsx("span", { className: "ds-msg-group__summary", children: summary }),
              badge && /* @__PURE__ */ jsx("code", { className: "ds-msg-group__badge", children: badge }),
              counter && /* @__PURE__ */ jsx("span", { className: "ds-msg-group__counter", children: counter }),
              status === "running" && /* @__PURE__ */ jsx("span", { className: "ds-msg-group__status", children: /* @__PURE__ */ jsx(SpinnerWithModes, { mode: "busy", size: "sm" }) }),
              status === "done" && /* @__PURE__ */ jsx(
                "span",
                {
                  className: "ds-msg-group__status ds-msg-group__status--done",
                  "aria-label": "\u0432\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u043E",
                  children: "\u2713"
                }
              ),
              status === "error" && /* @__PURE__ */ jsx(
                "span",
                {
                  className: "ds-msg-group__status ds-msg-group__status--error",
                  "aria-label": "\u043E\u0448\u0438\u0431\u043A\u0430",
                  children: "\u2717"
                }
              )
            ]
          }
        ),
        open && /* @__PURE__ */ jsx("div", { className: "ds-msg-group__body", children })
      ]
    }
  );
}
function AppShell({
  header,
  sidebar,
  rightPanel,
  sidebarWidth = 240,
  rightPanelWidth = 320,
  children,
  className
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn("ds-app-shell", header ? "ds-app-shell--has-header" : void 0, className),
      style: {
        "--ds-shell-sidebar": `${sidebarWidth}px`,
        "--ds-shell-right": rightPanel ? `${rightPanelWidth}px` : "0px"
      },
      children: [
        header && /* @__PURE__ */ jsx("header", { className: "ds-app-shell__header", children: header }),
        sidebar && /* @__PURE__ */ jsx("aside", { className: "ds-app-shell__sidebar", children: sidebar }),
        /* @__PURE__ */ jsx("main", { className: "ds-app-shell__main", children }),
        rightPanel && /* @__PURE__ */ jsx("aside", { className: "ds-app-shell__right", children: rightPanel })
      ]
    }
  );
}
function SidebarNav({ items, activeId, onSelect, footer }) {
  return /* @__PURE__ */ jsxs("nav", { className: "ds-sidebar", "aria-label": "\u0413\u043B\u0430\u0432\u043D\u0430\u044F \u043D\u0430\u0432\u0438\u0433\u0430\u0446\u0438\u044F", children: [
    /* @__PURE__ */ jsx("ul", { className: "ds-sidebar__list", children: items.map((item) => {
      const Icon = item.icon;
      const isActive = item.id === activeId;
      return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          className: cn("ds-sidebar__item", isActive && "ds-sidebar__item--active"),
          "aria-current": isActive ? "page" : void 0,
          onClick: () => onSelect(item.id),
          children: [
            Icon && /* @__PURE__ */ jsx(Icon, { size: 16, className: "ds-sidebar__item-icon" }),
            /* @__PURE__ */ jsx("span", { className: "ds-sidebar__item-label", children: item.label }),
            item.badge != null && /* @__PURE__ */ jsx("span", { className: "ds-sidebar__item-badge", children: item.badge })
          ]
        }
      ) }, item.id);
    }) }),
    footer && /* @__PURE__ */ jsx("div", { className: "ds-sidebar__footer", children: footer })
  ] });
}
function Header({ brand, center, actions, className }) {
  return /* @__PURE__ */ jsxs("div", { className: cn("ds-header", className), children: [
    brand && /* @__PURE__ */ jsx("div", { className: "ds-header__brand", children: brand }),
    center && /* @__PURE__ */ jsx("div", { className: "ds-header__center", children: center }),
    actions && /* @__PURE__ */ jsx("div", { className: "ds-header__actions", children: actions })
  ] });
}
var STORAGE_KEY = "iam.ds.theme";
function readStored() {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "dark" || v === "light" || v === "sovereign-dark") return v;
  } catch {
  }
  return null;
}
function useTheme(initial = "dark") {
  const [theme, setThemeState] = useState(() => readStored() ?? initial);
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", theme);
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
    }
  }, [theme]);
  const setTheme = useCallback((next) => setThemeState(next), []);
  const toggle = useCallback(() => {
    setThemeState((current) => current === "dark" ? "light" : "dark");
  }, []);
  return { theme, setTheme, toggle };
}
var _toasts = [];
var listeners = /* @__PURE__ */ new Set();
function emit() {
  for (const l of listeners) l(_toasts);
}
function nextId() {
  return `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}
var toast = {
  show(t) {
    const entry = { id: nextId(), durationMs: 4e3, variant: "info", ...t };
    _toasts = [..._toasts, entry];
    emit();
    return entry.id;
  },
  success(title, description) {
    return toast.show({ variant: "success", title, description });
  },
  warning(title, description) {
    return toast.show({ variant: "warning", title, description });
  },
  error(title, description) {
    return toast.show({ variant: "danger", title, description });
  },
  info(title, description) {
    return toast.show({ variant: "info", title, description });
  },
  dismiss(id) {
    _toasts = _toasts.filter((t) => t.id !== id);
    emit();
  }
};
function useToast() {
  const [toasts, setToasts] = useState(_toasts);
  useEffect(() => {
    const l = (t) => setToasts(t);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  const dismiss = useCallback((id) => toast.dismiss(id), []);
  return { toasts, dismiss, toast };
}

// src/i18n/catalogs/ru.json
var ru_default = {
  "common.cancel": "\u041E\u0442\u043C\u0435\u043D\u0430",
  "common.confirm": "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C",
  "common.close": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C",
  "common.save": "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C",
  "common.delete": "\u0423\u0434\u0430\u043B\u0438\u0442\u044C",
  "common.search": "\u041F\u043E\u0438\u0441\u043A",
  "common.loading": "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u044E\u2026",
  "common.error": "\u041E\u0448\u0438\u0431\u043A\u0430",
  "common.retry": "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C",
  "locale.switcher": "\u042F\u0437\u044B\u043A \u0438\u043D\u0442\u0435\u0440\u0444\u0435\u0439\u0441\u0430",
  "locale.russian": "\u0420\u0443\u0441\u0441\u043A\u0438\u0439",
  "locale.english": "English",
  "locale.switch_to_russian": "\u041F\u0435\u0440\u0435\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u043D\u0430 \u0440\u0443\u0441\u0441\u043A\u0438\u0439",
  "locale.switch_to_english": "\u041F\u0435\u0440\u0435\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u043D\u0430 \u0430\u043D\u0433\u043B\u0438\u0439\u0441\u043A\u0438\u0439",
  "approval.title": "\u0422\u0440\u0435\u0431\u0443\u0435\u0442\u0441\u044F \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0435",
  "approval.approve": "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C",
  "approval.deny": "\u041E\u0442\u043A\u043B\u043E\u043D\u0438\u0442\u044C",
  "approval.comment_placeholder": "\u041A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0439 (\u043E\u043F\u0446\u0438\u043E\u043D\u0430\u043B\u044C\u043D\u043E)\u2026",
  "approval.reason_placeholder": "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043F\u0440\u0438\u0447\u0438\u043D\u0443 \u043E\u0442\u043A\u043B\u043E\u043D\u0435\u043D\u0438\u044F\u2026",
  "approval.expires_in": "\u0438\u0441\u0442\u0435\u043A\u0430\u0435\u0442 \u0447\u0435\u0440\u0435\u0437",
  "approval.expired": "\u0417\u0430\u043F\u0440\u043E\u0441 \u0438\u0441\u0442\u0451\u043A",
  "cost_meter.aria_label": "\u0421\u0447\u0451\u0442\u0447\u0438\u043A \u0440\u0430\u0441\u0445\u043E\u0434\u043E\u0432",
  "cost_meter.warning_threshold": "\u0414\u043E\u0441\u0442\u0438\u0433\u043D\u0443\u0442 \u043F\u043E\u0440\u043E\u0433 80% \u0431\u044E\u0434\u0436\u0435\u0442\u0430",
  "cost_meter.hard_stop": "\u0411\u044E\u0434\u0436\u0435\u0442 \u0438\u0441\u0447\u0435\u0440\u043F\u0430\u043D",
  "cost_meter.empty": "\u041D\u0435\u0442 \u0432\u044B\u0437\u043E\u0432\u043E\u0432 \u0432 \u0442\u0435\u043A\u0443\u0449\u0435\u0439 \u0441\u0435\u0441\u0441\u0438\u0438",
  "workspace.switch": "\u0421\u043C\u0435\u043D\u0438\u0442\u044C workspace",
  "workspace.create": "\u0421\u043E\u0437\u0434\u0430\u0442\u044C workspace",
  "workspace.recent": "\u041D\u0435\u0434\u0430\u0432\u043D\u0438\u0435",
  "workspace.all": "\u0412\u0441\u0435 workspaces",
  "workspace.search_placeholder": "\u041F\u043E\u0438\u0441\u043A workspaces\u2026",
  "doc_picker.title": "\u041F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u044B",
  "doc_picker.search": "\u041F\u043E\u0438\u0441\u043A \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u0432\u2026",
  "doc_picker.upload": "\u0417\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C",
  "doc_picker.attach_url": "\u0418\u043B\u0438 \u043F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C \u043F\u043E URL",
  "doc_picker.empty": "\u0412 \u044D\u0442\u043E\u043C workspace \u043F\u043E\u043A\u0430 \u043D\u0435\u0442 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u0432.",
  "doc_picker.no_match": "\u0421\u043E\u0432\u043F\u0430\u0434\u0435\u043D\u0438\u0439 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E.",
  "doc_picker.no_access": "\u041D\u0435\u0442 \u0434\u043E\u0441\u0442\u0443\u043F\u0430",
  "composer.placeholder": "\u041E\u043F\u0438\u0448\u0438\u0442\u0435 \u0437\u0430\u0434\u0430\u0447\u0443 \u0438\u043B\u0438 \u0437\u0430\u0434\u0430\u0439\u0442\u0435 \u0432\u043E\u043F\u0440\u043E\u0441\u2026",
  "composer.send": "\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C",
  "composer.attach": "\u041F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C \u0444\u0430\u0439\u043B",
  "composer.voice": "\u0413\u043E\u043B\u043E\u0441\u043E\u0432\u043E\u0439 \u0432\u0432\u043E\u0434",
  "composer.mode.plan": "\u041F\u043B\u0430\u043D",
  "composer.mode.auto": "\u0410\u0432\u0442\u043E",
  "composer.mode.confirm": "\u041F\u043E\u0434\u0442\u0432.",
  "composer.mode.research": "\u0418\u0441\u0441\u043B\u0435\u0434.",
  "composer.mode.chat": "\u0427\u0430\u0442"
};

// src/i18n/catalogs/en.json
var en_default = {
  "common.cancel": "Cancel",
  "common.confirm": "Confirm",
  "common.close": "Close",
  "common.save": "Save",
  "common.delete": "Delete",
  "common.search": "Search",
  "common.loading": "Loading\u2026",
  "common.error": "Error",
  "common.retry": "Retry",
  "locale.switcher": "Interface language",
  "locale.russian": "Russian",
  "locale.english": "English",
  "locale.switch_to_russian": "Switch to Russian",
  "locale.switch_to_english": "Switch to English",
  "approval.title": "Approval required",
  "approval.approve": "Approve",
  "approval.deny": "Deny",
  "approval.comment_placeholder": "Optional comment\u2026",
  "approval.reason_placeholder": "Reason for denying\u2026",
  "approval.expires_in": "expires in",
  "approval.expired": "Request expired",
  "cost_meter.aria_label": "Cost meter",
  "cost_meter.warning_threshold": "80% of budget reached",
  "cost_meter.hard_stop": "Budget exhausted",
  "cost_meter.empty": "No tool calls yet in this session",
  "workspace.switch": "Switch workspace",
  "workspace.create": "Create workspace",
  "workspace.recent": "Recent",
  "workspace.all": "All workspaces",
  "workspace.search_placeholder": "Search workspaces\u2026",
  "doc_picker.title": "Attach documents",
  "doc_picker.search": "Search documents\u2026",
  "doc_picker.upload": "Upload",
  "doc_picker.attach_url": "Or attach by URL",
  "doc_picker.empty": "No documents in this workspace yet.",
  "doc_picker.no_match": "No matches found.",
  "doc_picker.no_access": "No access",
  "composer.placeholder": "Describe the task or ask a question\u2026",
  "composer.send": "Send",
  "composer.attach": "Attach file",
  "composer.voice": "Voice input",
  "composer.mode.plan": "Plan",
  "composer.mode.auto": "Auto",
  "composer.mode.confirm": "Confirm",
  "composer.mode.research": "Research",
  "composer.mode.chat": "Chat"
};
var DEFAULT_STORAGE_KEY = "iam.locale";
var DEFAULT_CATALOGS = {
  ru: ru_default,
  en: en_default
};
var I18nContext = createContext(void 0);
function normalizeLocale(value, fallback = "ru") {
  const normalized = value?.trim().toLowerCase();
  if (normalized?.startsWith("en")) return "en";
  if (normalized?.startsWith("ru")) return "ru";
  return fallback;
}
function resolveInitialLocale(storageKey, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const persisted = window.localStorage.getItem(storageKey);
    if (persisted) return normalizeLocale(persisted, fallback);
  } catch {
  }
  const candidates = window.navigator.languages?.length ? window.navigator.languages : [window.navigator.language];
  for (const candidate of candidates) {
    const normalized = candidate?.trim().toLowerCase();
    if (normalized?.startsWith("en")) return "en";
    if (normalized?.startsWith("ru")) return "ru";
  }
  return fallback;
}
function I18nProvider({
  locale: controlledLocale,
  defaultLocale = "ru",
  storageKey = DEFAULT_STORAGE_KEY,
  messages,
  onLocaleChange,
  children
}) {
  const [internalLocale, setInternalLocale] = useState(
    () => resolveInitialLocale(storageKey, defaultLocale)
  );
  const locale = controlledLocale ?? internalLocale;
  const merged = useMemo(() => {
    const out = { ...DEFAULT_CATALOGS };
    if (messages) {
      for (const loc of ["ru", "en"]) {
        const cat = messages[loc];
        if (!cat) continue;
        out[loc] = { ...out[loc] ?? {}, ...cat };
      }
    }
    return out;
  }, [messages]);
  const t = useCallback(
    (key, params) => {
      const primary = merged[locale]?.[key];
      const fallback = merged.en?.[key];
      let raw = primary ?? fallback ?? key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          raw = raw.replaceAll(`{${k}}`, String(v));
        }
      }
      return raw;
    },
    [merged, locale]
  );
  const setLocale = useCallback(
    (nextLocale) => {
      const normalized = normalizeLocale(nextLocale, defaultLocale);
      if (controlledLocale === void 0) setInternalLocale(normalized);
      try {
        window.localStorage.setItem(storageKey, normalized);
      } catch {
      }
      onLocaleChange?.(normalized);
    },
    [controlledLocale, defaultLocale, onLocaleChange, storageKey]
  );
  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      window.localStorage.setItem(storageKey, locale);
    } catch {
    }
  }, [locale, storageKey]);
  const ctx = useMemo(
    () => ({
      locale,
      setLocale,
      t
    }),
    [locale, setLocale, t]
  );
  return /* @__PURE__ */ jsx(I18nContext.Provider, { value: ctx, children });
}
function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    return (key) => key;
  }
  return ctx.t;
}
function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within <I18nProvider>");
  }
  return ctx;
}
function LocaleSwitcher({ compact = true, className = "", ...props }) {
  const { locale, setLocale, t } = useI18n();
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...props,
      className: `iam-locale-switcher${compact ? " is-compact" : ""} ${className}`.trim(),
      role: "group",
      "aria-label": t("locale.switcher"),
      children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: locale === "ru" ? "is-active" : "",
            "aria-pressed": locale === "ru",
            "aria-label": locale === "ru" ? t("locale.russian") : t("locale.switch_to_russian"),
            onClick: () => setLocale("ru"),
            children: "RU"
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: locale === "en" ? "is-active" : "",
            "aria-pressed": locale === "en",
            "aria-label": locale === "en" ? t("locale.english") : t("locale.switch_to_english"),
            onClick: () => setLocale("en"),
            children: "EN"
          }
        )
      ]
    }
  );
}
var LEGAL_SERVICES = {
  "2mind": { name: "2Mind", legalBaseUrl: "https://2mind.one" },
  agl: { name: "AGL", legalBaseUrl: "https://iamsolution.ru" },
  "iam-agent": { name: "IAM Agent", legalBaseUrl: "https://agent.iamgroup.ru" },
  "iam-comm": { name: "IAM.Comm", legalBaseUrl: "https://iamcomm.ru" },
  "iam-comm-meet": { name: "IAM.Comm Meet", legalBaseUrl: "https://iamcomm.ru" },
  "iam-core": { name: "IAM.Core", legalBaseUrl: "https://iamgroup.ru" },
  "iam-docs": { name: "IAM.Docs", legalBaseUrl: "https://iamgroup.ru" },
  "iam-group": { name: "IAM Group", legalBaseUrl: "https://iamgroup.ru" },
  "iam-hosting": { name: "IAM.Hosting", legalBaseUrl: "https://iamgroup.ru" },
  "iam-identity": { name: "IAM.Identity", legalBaseUrl: "https://iamgroup.ru" },
  "iam-market": { name: "IAM.Market", legalBaseUrl: "https://iam.market" },
  "iam-mind": { name: "IAM.Mind", legalBaseUrl: "https://iammind.ru" },
  "iam-router": { name: "IAM.Router", legalBaseUrl: "https://iamrouter.ru" },
  "iam-secure": { name: "IAM.Secure", legalBaseUrl: "https://iamsecure.ru" },
  "iam-speech": { name: "IAM.Speech", legalBaseUrl: "https://iamspeech.ru" },
  "iam-voice": { name: "IAM.Voice", legalBaseUrl: "https://iamvoice.ru" },
  iambot: { name: "IAM.Bot", legalBaseUrl: "https://iambot.ru" }
};
var ROOT_STYLE = {
  bottom: 10,
  color: "var(--iam-ink-mute, #a8b0c2)",
  fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
  fontSize: 11,
  lineHeight: 1.45,
  maxWidth: "min(360px, calc(100vw - 24px))",
  position: "fixed",
  right: 12,
  zIndex: 90
};
var DETAILS_STYLE = {
  backdropFilter: "blur(18px)",
  background: "color-mix(in srgb, var(--iam-bg-raised, #111827) 92%, transparent)",
  border: "1px solid var(--iam-border, rgba(148, 163, 184, 0.24))",
  borderRadius: 10,
  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.28)",
  overflow: "hidden"
};
var SUMMARY_STYLE = {
  color: "var(--iam-ink-mute, #a8b0c2)",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.02em",
  padding: "7px 10px"
};
var CONTENT_STYLE = {
  borderTop: "1px solid var(--iam-border, rgba(148, 163, 184, 0.18))",
  display: "grid",
  gap: 7,
  padding: "10px 12px 12px"
};
var LINK_ROW_STYLE = {
  display: "flex",
  flexWrap: "wrap",
  gap: "5px 10px"
};
var LINK_STYLE = {
  color: "var(--iam-accent, #8aa4ff)",
  textDecoration: "none"
};
function buildLegalDocumentUrl(serviceKey, documentKind, locale = "ru") {
  const service = LEGAL_SERVICES[serviceKey];
  const url = new URL(`/${documentKind}`, service.legalBaseUrl);
  url.searchParams.set("service", serviceKey);
  if (locale === "en") url.searchParams.set("lang", "en");
  return url.toString();
}
function useDocumentLocale() {
  const readLocale = () => typeof document !== "undefined" && document.documentElement.lang.toLowerCase().startsWith("en") ? "en" : "ru";
  const [locale, setLocale] = useState(readLocale);
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
    const observer = new MutationObserver(() => setLocale(readLocale()));
    observer.observe(document.documentElement, { attributeFilter: ["lang"], attributes: true });
    return () => observer.disconnect();
  }, []);
  return locale;
}
function LegalNotice({ serviceKey }) {
  const service = LEGAL_SERVICES[serviceKey];
  const locale = useDocumentLocale();
  const isEnglish = locale === "en";
  const isTwoMind = serviceKey === "2mind";
  const labels = isTwoMind ? isEnglish ? { offer: "Early Access Terms", privacy: "Privacy Notice", contacts: "Contact" } : {
    offer: "\u0423\u0441\u043B\u043E\u0432\u0438\u044F \u0440\u0430\u043D\u043D\u0435\u0433\u043E \u0434\u043E\u0441\u0442\u0443\u043F\u0430",
    privacy: "\u041A\u043E\u043D\u0444\u0438\u0434\u0435\u043D\u0446\u0438\u0430\u043B\u044C\u043D\u043E\u0441\u0442\u044C",
    contacts: "\u0421\u0432\u044F\u0437\u0430\u0442\u044C\u0441\u044F \u0441 \u043A\u043E\u043C\u0430\u043D\u0434\u043E\u0439"
  } : isEnglish ? {
    offer: "Terms of Service",
    privacy: "Personal Data Policy",
    contacts: "Contacts and company details"
  } : {
    offer: "\u041F\u0443\u0431\u043B\u0438\u0447\u043D\u0430\u044F \u043E\u0444\u0435\u0440\u0442\u0430",
    privacy: "\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0438 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445",
    contacts: "\u041A\u043E\u043D\u0442\u0430\u043A\u0442\u044B \u0438 \u0440\u0435\u043A\u0432\u0438\u0437\u0438\u0442\u044B"
  };
  const legalLabel = isEnglish ? "Legal information" : "\u041F\u0440\u0430\u0432\u043E\u0432\u0430\u044F \u0438\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u044F";
  return /* @__PURE__ */ jsx("aside", { "aria-label": legalLabel, lang: locale, style: ROOT_STYLE, children: /* @__PURE__ */ jsxs("details", { style: DETAILS_STYLE, children: [
    /* @__PURE__ */ jsx("summary", { style: SUMMARY_STYLE, children: legalLabel }),
    /* @__PURE__ */ jsxs("div", { style: CONTENT_STYLE, children: [
      /* @__PURE__ */ jsx("div", { style: LINK_ROW_STYLE, children: Object.keys(labels).map((documentKind) => /* @__PURE__ */ jsx(
        "a",
        {
          href: buildLegalDocumentUrl(serviceKey, documentKind, locale),
          rel: "noopener",
          style: LINK_STYLE,
          target: "_blank",
          children: labels[documentKind]
        },
        documentKind
      )) }),
      isTwoMind ? /* @__PURE__ */ jsxs("span", { children: [
        isEnglish ? "2mind is an international product direction in limited early access. Product, privacy, and early-access enquiries: " : "2mind \u2014 \u043C\u0435\u0436\u0434\u0443\u043D\u0430\u0440\u043E\u0434\u043D\u043E\u0435 \u043F\u0440\u043E\u0434\u0443\u043A\u0442\u043E\u0432\u043E\u0435 \u043D\u0430\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u0435 \u0441 \u043E\u0433\u0440\u0430\u043D\u0438\u0447\u0435\u043D\u043D\u044B\u043C \u0440\u0430\u043D\u043D\u0438\u043C \u0434\u043E\u0441\u0442\u0443\u043F\u043E\u043C. \u0412\u043E\u043F\u0440\u043E\u0441\u044B \u043E \u043F\u0440\u043E\u0434\u0443\u043A\u0442\u0435, \u043F\u0440\u0438\u0432\u0430\u0442\u043D\u043E\u0441\u0442\u0438 \u0438 \u0440\u0430\u043D\u043D\u0435\u043C \u0434\u043E\u0441\u0442\u0443\u043F\u0435: ",
        /* @__PURE__ */ jsx("a", { href: "mailto:mind@iamsolution.ru", style: LINK_STYLE, children: "mind@iamsolution.ru" }),
        "."
      ] }) : /* @__PURE__ */ jsxs("span", { children: [
        isEnglish ? `${service.name}: services are provided by individual entrepreneur Andrey Nikolaevich Morozov, TIN 773272773292, OGRNIP 317774600281613. Using the service constitutes acceptance of the Terms of Service and consent to personal-data processing under the Policy. Payment and personal-data enquiries: ` : `${service.name}: \u0443\u0441\u043B\u0443\u0433\u0438 \u043E\u043A\u0430\u0437\u044B\u0432\u0430\u0435\u0442 \u0418\u041F \u041C\u043E\u0440\u043E\u0437\u043E\u0432 \u0410\u043D\u0434\u0440\u0435\u0439 \u041D\u0438\u043A\u043E\u043B\u0430\u0435\u0432\u0438\u0447, \u0418\u041D\u041D 773272773292, \u041E\u0413\u0420\u041D\u0418\u041F 317774600281613. \u0418\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u043D\u0438\u0435 \u0441\u0435\u0440\u0432\u0438\u0441\u0430 \u043E\u0437\u043D\u0430\u0447\u0430\u0435\u0442 \u0430\u043A\u0446\u0435\u043F\u0442 \u043F\u0443\u0431\u043B\u0438\u0447\u043D\u043E\u0439 \u043E\u0444\u0435\u0440\u0442\u044B \u0438 \u0441\u043E\u0433\u043B\u0430\u0441\u0438\u0435 \u043D\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0443 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445 \u043D\u0430 \u0443\u0441\u043B\u043E\u0432\u0438\u044F\u0445 \u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0438. \u041F\u043E \u0432\u043E\u043F\u0440\u043E\u0441\u0430\u043C \u043E\u043F\u043B\u0430\u0442\u044B \u0438 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445: `,
        /* @__PURE__ */ jsx("a", { href: "mailto:info@iamgroup.ru", style: LINK_STYLE, children: "info@iamgroup.ru" }),
        "."
      ] })
    ] })
  ] }) });
}

export { AgentProgressCard, AppShell, ApprovalPrompt, Badge, Button, ChatTabs, Composer, CostMeter, DesignSystemProvider, DocumentPicker, GlassPanel, Header, I18nProvider, IconButton, Input, Kbd, LegalNotice, LocaleSwitcher, MessageBubble, MessageGroup, SidebarNav, Spinner, SpinnerWithModes, StatusBadge, StreamingMessage, ThinkingBlock, Toast, ToolCallTimeline, ToolUseCard, Tooltip, WorkspaceSwitcher, buildLegalDocumentUrl, cn, colors, fontSizes, fontWeights, lineHeights, motion, normalizeLocale, radii, shadows, spacing, toast, tokens, useDesignSystem, useI18n, useTheme, useToast, useTranslation, zIndex };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map